import styled from "styled-components";
import { useEffect, useState } from "react";
import { X, Check } from "lucide-react";
import { router } from "@inertiajs/react";

import { theme } from "./theme";
import { useT } from "./i18n";
import { apiFetch } from "./api";

// Move an operator block from this room to another of the SAME TYPE on the SAME DATE, carrying its
// reason and note. Deliberately a near-copy of RoomMoveModal: same skeleton, same row primitives,
// same two-phase confirm in one panel — the operator is doing the same shape of thing (pick a room of
// this type, confirm), and two dialogs that behaved differently would be the surprise.
//
// The move itself pushes NOTHING to eZee; see FrontOffice::Rooms::MoveBlockService for why.
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(26, 26, 26, 0.5);
  backdrop-filter: blur(2px);
`;

const Panel = styled.div`
  width: 100%;
  max-width: 640px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 14px;
  border-top: 3px solid ${theme.gold};
  box-shadow: 0 24px 60px -18px rgba(26, 26, 26, 0.5);
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.9rem 1rem 0.6rem;
  border-bottom: 1px solid ${theme.border};
`;

const Title = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.85rem 1.1rem;
`;

const Intro = styled.p`
  margin: 0 0 0.7rem;
  font-size: 0.84rem;
  line-height: 1.5;
  color: ${theme.ink700};
`;

const Muted = styled.div`
  padding: 0.6rem 0.25rem;
  font-size: 0.9rem;
  color: ${theme.muted};
`;

const ErrorNote = styled.div`
  margin-bottom: 0.7rem;
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  background: #fdecec;
  border: 1px solid #f2c4c0;
  color: #b91c1c;
  font-size: 0.85rem;
  font-weight: 600;
`;

// Amber, not red: moving a block off a transit-held room is allowed and sometimes correct. It is a
// consequence the operator should see, never a refusal.
const WarnNote = styled.div`
  margin-top: 0.7rem;
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  background: #fff8e6;
  border: 1px solid #f2d492;
  color: #8a4b00;
  font-size: 0.83rem;
  line-height: 1.45;
`;

const BuildingBlock = styled.div`
  margin-bottom: 1rem;
`;

const BuildingName = styled.div`
  margin-bottom: 0.3rem;
  font-size: 0.86rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const FloorGroup = styled.div`
  & + & {
    margin-top: 0.55rem;
  }
`;

const FloorHeading = styled.div`
  padding: 0.25rem 0.1rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${theme.muted};
`;

const RoomList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  overflow: hidden;
`;

const RoomRow = styled.button`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  padding: 0.5rem 0.7rem;
  border: 0;
  border-top: 1px solid ${theme.border};
  background: ${(props) =>
    props.$selected ? "#e8f5e9" : props.$current ? "#eef5fd" : "#ffffff"};
  box-shadow: ${(props) =>
    props.$selected ? "inset 3px 0 0 #4caf50" : "none"};
  cursor: ${(props) => (props.$selectable ? "pointer" : "default")};
  text-align: left;
  &:first-child {
    border-top: 0;
  }
  &:hover {
    background: ${(props) =>
      props.$selectable && !props.$selected ? "#f6faf6" : undefined};
  }
`;

const RoomCode = styled.div`
  flex: 0 0 3.1rem;
  font-size: 0.95rem;
  font-weight: 800;
  color: ${(props) => (props.$dim ? theme.muted : theme.ink)};
`;

const RoomInfo = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const RoomNote = styled.div`
  font-size: 0.8rem;
  line-height: 1.35;
  color: ${(props) => props.$color || theme.muted};
`;

const TickSlot = styled.span`
  flex: 0 0 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2e7d32;
`;

const ConfirmBox = styled.div`
  padding: 0.7rem 0.85rem;
  border-radius: 8px;
  background: ${theme.goldSoft};
  border-left: 4px solid ${theme.goldDark};
  font-size: 0.92rem;
  line-height: 1.5;
  color: ${theme.ink};
`;

const Carrying = styled.div`
  margin-top: 0.45rem;
  font-size: 0.83rem;
  color: ${theme.ink700};
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 1.1rem;
  border-top: 1px solid ${theme.border};
`;

const Selected = styled.span`
  margin-right: auto;
  font-size: 0.84rem;
  color: ${theme.muted};
`;

const Cancel = styled.button`
  padding: 0.45rem 1rem;
  border: 1px solid ${theme.border};
  border-radius: 9px;
  background: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
  color: ${theme.ink700};
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Confirm = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 1.1rem;
  border: 0;
  border-radius: 9px;
  background: ${theme.ink};
  color: ${theme.gold};
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${theme.inkSoft};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const MoveBlockModal = (props) => {
  const { t } = useT();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [processing, setProcessing] = useState(false);

  const roomId = props.room?.id;
  const date = props.date;

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError("");
      setPayload(null);
      setSelected(null);
      setConfirming(false);
      try {
        const res = await apiFetch(
          `/frontdesk_v2/rooms/${roomId}/block_move_targets?date=${date}`,
        );
        if (!active) {
          return;
        }
        if (res && res.data) {
          setPayload(res.data);
        } else {
          setError(res?.message || t("error_body"));
        }
      } catch {
        if (active) {
          setError(t("error_body"));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [roomId, date, t]);

  const submit = () => {
    setProcessing(true);
    // Redirect + flash, the same path every other rack write takes — the toast and the grid reload
    // both come from the controller, so there is no second source of truth for the result.
    router.post(
      `/frontdesk_v2/rooms/${roomId}/move_block`,
      {
        date,
        target_room_id: selected?.id,
        return_to: window.location.pathname + window.location.search,
      },
      { preserveScroll: true },
    );
    props.onClose();
  };

  // The SOURCE room's hours. Moving the block away re-opens this room to the overnight auto-assigner
  // for exactly those hours, which is the one consequence of this action that is not visible on screen.
  const sourceHasTransit = (payload?.rooms || []).some(
    (room) => room.current && room.transit_note,
  );

  const carrying = [props.room?.block_reason, props.room?.block_note]
    .filter(Boolean)
    .join(" — ");

  const renderRoom = (room) => {
    const selectable = room.eligible && !room.current;
    const isSelected = selected?.id === room.id;

    return (
      <RoomRow
        key={room.id}
        type="button"
        $selectable={selectable}
        $selected={isSelected}
        $current={room.current}
        disabled={!selectable}
        onClick={() => setSelected({ id: room.id, code: room.code })}
      >
        <RoomCode $dim={!room.eligible && !room.current}>{room.code}</RoomCode>
        <RoomInfo>
          {room.current ? (
            <RoomNote $color="#1d5fa8">{t("change_room_current")}</RoomNote>
          ) : !room.eligible ? (
            <RoomNote>{room.reason || t("change_room_unavailable")}</RoomNote>
          ) : null}
          {room.transit_note ? (
            <RoomNote $color="#0284c7">{t("move_block_transit_note")}</RoomNote>
          ) : null}
        </RoomInfo>
        <TickSlot>{isSelected ? <Check size={14} /> : null}</TickSlot>
      </RoomRow>
    );
  };

  const renderList = () => {
    const buildings = payload?.buildings || [];
    if (buildings.length === 0) {
      return <Muted>{t("move_block_none")}</Muted>;
    }
    return buildings.map((building) => (
      <BuildingBlock key={building.id || building.name}>
        <BuildingName>{building.name || "—"}</BuildingName>
        {building.floors.map((floor) => (
          <FloorGroup key={floor.id || floor.code}>
            <FloorHeading>
              {t("move_room_floor")} {floor.code || floor.name || ""}
            </FloorHeading>
            <RoomList>{floor.rooms.map(renderRoom)}</RoomList>
          </FloorGroup>
        ))}
      </BuildingBlock>
    ));
  };

  return (
    <Overlay onClick={props.onClose}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>
            {t("move_block_title")}
            {props.room?.code ? ` — ${props.room.code}` : ""}
          </Title>
          <IconButton
            type="button"
            aria-label={t("close")}
            onClick={props.onClose}
          >
            <X size={16} />
          </IconButton>
        </Head>
        <Body>
          <Intro>{t("move_block_intro")}</Intro>
          {error ? <ErrorNote>{error}</ErrorNote> : null}
          {loading ? (
            <Muted>{t("loading")}</Muted>
          ) : confirming ? (
            <>
              <ConfirmBox>
                {t("move_block_confirm")} {props.room?.code} → {selected?.code}{" "}
                ({payload?.date || date})
                {carrying ? (
                  <Carrying>
                    {t("move_block_carrying_reason")} {carrying}
                  </Carrying>
                ) : null}
              </ConfirmBox>
              {sourceHasTransit ? (
                <WarnNote>{t("move_block_source_transit_warning")}</WarnNote>
              ) : null}
            </>
          ) : (
            renderList()
          )}
        </Body>
        <Foot>
          <Selected>
            {selected
              ? `${t("move_block_selected")}: ${selected.code || ""}`
              : ""}
          </Selected>
          <Cancel
            type="button"
            onClick={() =>
              confirming ? setConfirming(false) : props.onClose()
            }
          >
            {t("confirm_cancel")}
          </Cancel>
          <Confirm
            type="button"
            disabled={!selected || processing}
            onClick={() => (confirming ? submit() : setConfirming(true))}
          >
            <Check size={15} />
            {t("move_block_ok")}
          </Confirm>
        </Foot>
      </Panel>
    </Overlay>
  );
};

export default MoveBlockModal;
